import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Wallet,
    Plus,
    Trash2,
    Smartphone,
    Banknote,
    Landmark,
    ScrollText,
    HelpCircle,
} from 'lucide-react';
import { api, fmtMoney, fmtDateTime } from '../lib/api';
import { RecordPaymentDialog } from './RecordPaymentDialog';

function MethodBadge({ method }) {
    const map = {
        upi: { icon: Smartphone, label: 'UPI' },
        cash: { icon: Banknote, label: 'Cash' },
        bank: { icon: Landmark, label: 'Bank' },
        cheque: { icon: ScrollText, label: 'Cheque' },
    };
    const m = map[method] || { icon: HelpCircle, label: method || 'Other' };
    const Icon = m.icon;
    return (
        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-background/80 px-2 py-0.5 text-[11px] uppercase tracking-wide">
            <Icon size={11} /> {m.label}
        </span>
    );
}

function BalanceStat({ label, value, tone = 'default', testid }) {
    const toneCls =
        tone === 'positive'
            ? 'text-[hsl(var(--price-decrease-fg))]'
            : tone === 'negative'
                ? 'text-[hsl(var(--price-increase-fg))]'
                : '';
    return (
        <div className="p-4 rounded-lg border border-border bg-card/70">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`font-heading text-2xl mt-1 tabular-nums font-mono ${toneCls}`} data-testid={testid}>
                {value}
            </div>
        </div>
    );
}

/**
 * VendorPaymentsPanel
 *
 * Props:
 *  - vendor { vendor, vendor_key, total_spend, total_paid, outstanding, upi_id, upi_name, phone }
 *  - onChange() — called after any mutation (payment add/delete) so parent can refresh totals
 */
export function VendorPaymentsPanel({ vendor, onChange }) {
    const [payments, setPayments] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null); // payment object or null
    const [deleting, setDeleting] = useState(false);
    const [meta, setMeta] = useState({
        upi_id: vendor?.upi_id || '',
        upi_name: vendor?.upi_name || '',
    });

    useEffect(() => {
        setMeta({
            upi_id: vendor?.upi_id || '',
            upi_name: vendor?.upi_name || '',
        });
    }, [vendor?.upi_id, vendor?.upi_name]);

    async function load() {
        if (!vendor?.vendor_key) return;
        setLoading(true);
        try {
            const rows = await api.listVendorPayments(vendor.vendor_key);
            setPayments(rows || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load payments');
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [vendor?.vendor_key]);

    async function handleDelete() {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.deleteVendorPayment(confirmDelete.id);
            toast.success('Payment removed');
            setConfirmDelete(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to delete payment');
        } finally {
            setDeleting(false);
        }
    }

    const outstandingTone = useMemo(() => {
        const v = Number(vendor?.outstanding || 0);
        if (v > 0) return 'negative';
        if (v < 0) return 'positive';
        return 'default';
    }, [vendor?.outstanding]);

    const outstandingValue = Number(vendor?.outstanding || 0);
    const outstandingLabel =
        outstandingValue > 0
            ? `You owe ${fmtMoney(outstandingValue)}`
            : outstandingValue < 0
                ? `Advance ${fmtMoney(Math.abs(outstandingValue))}`
                : 'Fully settled';

    return (
        <Card className="p-5 border-border/80" data-testid="vendor-payments-panel">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-md bg-accent text-accent-foreground flex items-center justify-center">
                        <Wallet size={16} />
                    </div>
                    <div>
                        <div className="font-heading text-lg leading-none">Payments</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Track what you owe and what you've paid.
                        </div>
                    </div>
                </div>
                <Button
                    onClick={() => setOpenDialog(true)}
                    className="gap-1.5"
                    data-testid="vendor-record-payment-btn"
                >
                    <Plus size={14} /> Record payment
                </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <BalanceStat
                    label="Total billed"
                    value={fmtMoney(vendor?.total_spend || 0)}
                    testid="vendor-payments-billed"
                />
                <BalanceStat
                    label="Total paid"
                    value={fmtMoney(vendor?.total_paid || 0)}
                    testid="vendor-payments-paid"
                />
                <BalanceStat
                    label="Outstanding"
                    value={outstandingLabel}
                    tone={outstandingTone}
                    testid="vendor-payments-outstanding"
                />
            </div>

            {meta.upi_id && (
                <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <Smartphone size={12} />
                    Saved UPI:{' '}
                    <span className="font-mono text-foreground">{meta.upi_id}</span>
                    {meta.upi_name && <span>· {meta.upi_name}</span>}
                </div>
            )}

            <div className="mt-5">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                    Payment history
                </div>
                {loading ? (
                    <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-10" />
                        ))}
                    </div>
                ) : (payments || []).length === 0 ? (
                    <div className="p-6 rounded-md border border-dashed border-border text-sm text-muted-foreground text-center">
                        No payments yet. Click <b>Record payment</b> to log the first one.
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-md border border-border">
                        <table className="w-full text-sm" data-testid="vendor-payments-table">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Paid on</th>
                                    <th className="text-right px-3 py-2 font-medium">Amount</th>
                                    <th className="text-left px-3 py-2 font-medium">Method</th>
                                    <th className="text-left px-3 py-2 font-medium">Reference</th>
                                    <th className="text-left px-3 py-2 font-medium">Note</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments.map((p) => (
                                    <tr
                                        key={p.id}
                                        className="border-t border-border hover:bg-muted/40"
                                        data-testid={`vendor-payment-row-${p.id}`}
                                    >
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {fmtDateTime(p.paid_at)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums font-mono font-medium">
                                            {fmtMoney(p.amount)}
                                        </td>
                                        <td className="px-3 py-2">
                                            <MethodBadge method={p.method} />
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                                            {p.reference || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[220px]">
                                            {p.note || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setConfirmDelete(p)}
                                                aria-label="Delete payment"
                                                data-testid={`vendor-payment-delete-${p.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                <tr className="border-t border-border bg-muted/30 font-medium">
                                    <td className="px-3 py-2 uppercase text-xs tracking-wide text-muted-foreground">
                                        Total paid
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-mono">
                                        {fmtMoney(vendor?.total_paid || 0)}
                                    </td>
                                    <td colSpan={4}>
                                        <Badge
                                            className={`ml-2 ${outstandingTone === 'negative'
                                                ? 'bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] border border-[hsl(var(--price-increase-border))]'
                                                : outstandingTone === 'positive'
                                                    ? 'bg-[hsl(var(--price-decrease-bg))] text-[hsl(var(--price-decrease-fg))] border border-[hsl(var(--price-decrease-border))]'
                                                    : ''
                                                }`}
                                        >
                                            {outstandingLabel}
                                        </Badge>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <RecordPaymentDialog
                open={openDialog}
                onOpenChange={setOpenDialog}
                vendor={vendor}
                outstanding={vendor?.outstanding}
                meta={meta}
                onSuccess={async () => {
                    await load();
                    onChange?.();
                }}
                onMetaChange={(next) => setMeta({ upi_id: next?.upi_id || '', upi_name: next?.upi_name || '' })}
            />

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="vendor-payment-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this payment?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will delete the payment record of{' '}
                            <span className="font-mono">
                                {confirmDelete ? fmtMoney(confirmDelete.amount) : ''}
                            </span>{' '}
                            paid on{' '}
                            {confirmDelete ? fmtDateTime(confirmDelete.paid_at) : ''}. Vendor balance
                            will be recalculated.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDelete();
                            }}
                            disabled={deleting}
                            data-testid="vendor-payment-delete-confirm-btn"
                        >
                            {deleting ? 'Removing…' : 'Remove payment'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
